import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { investorLogin } from '../lib/api';
import { TrendingUp, Shield, DollarSign } from 'lucide-react';

export const InvestorLoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await investorLogin(username, password);
      // Store investor token separately
      localStorage.setItem('investor_token', response.data.token);
      localStorage.setItem('investor', JSON.stringify(response.data.investor));
      toast.success(`Welcome back, ${response.data.investor.name}!`);
      navigate('/investor-portal');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="p-2 bg-emerald-100 rounded-xl">
              <TrendingUp className="h-8 w-8 text-emerald-600" />
            </div>
          </div>
          <h1 className="text-4xl font-black tracking-tight text-emerald-700 mb-2" data-testid="investor-login-title">
            Investor Portal
          </h1>
          <p className="text-muted-foreground text-lg">Nexus Investment Management</p>
        </div>
        
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-emerald-100 shadow-xl p-8">
          <h2 className="text-2xl font-bold mb-6 text-slate-800">Investor Sign In</h2>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="username" data-testid="investor-username-label">Username or Email</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username or email"
                required
                data-testid="investor-username-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password" data-testid="investor-password-label">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="investor-password-input"
                className="mt-1"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11 font-semibold"
              disabled={loading}
              data-testid="investor-login-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          
          <div className="mt-6 pt-6 border-t border-slate-100">
            <p className="text-center text-sm text-muted-foreground">
              Are you an admin?{' '}
              <Link to="/login" className="text-emerald-600 hover:underline font-medium">
                Login here
              </Link>
            </p>
          </div>
        </div>
        
        {/* Feature highlights */}
        <div className="mt-8 grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-white/60 rounded-xl border border-slate-100">
            <Shield className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
            <span className="text-xs text-muted-foreground">Secure Access</span>
          </div>
          <div className="text-center p-4 bg-white/60 rounded-xl border border-slate-100">
            <TrendingUp className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
            <span className="text-xs text-muted-foreground">Track ROI</span>
          </div>
          <div className="text-center p-4 bg-white/60 rounded-xl border border-slate-100">
            <DollarSign className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
            <span className="text-xs text-muted-foreground">View Payouts</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorLoginPage;
